import {
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Optional,
  QueryList,
  Self,
  signal,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
  WritableSignal,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NgControl,
  UntypedFormControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { PlIcon } from '@app/icon-library/icons';
import { IconFrame } from '@app/icon-library/lib/pl-icon.component';
import { ButtonPadding, ButtonVariant } from '@app/components/button/button.component';
import { convert24hTimeToAmPm } from '@app/utils/date';
import { NgOnInitWriteBugAbstractComponent } from '@shared/abstract/ngOnInitWriteBug.abstract.component';
import { PositionX, PositionY } from '../pl-popover/pl-popover.component';
import { ComponentChanges } from '@app/utils/simpleChangesType';
import { addHours, addMinutes, differenceInMinutes, format, isBefore } from 'date-fns';
import { I18nService } from '@app/i18n';
import { opacityAnimation } from '@app/utils/animations';

let identifier: number = 0;

export enum TimeFormat {
  amPm = 'amPm',
  twentyFourHours = 'twentyFourHours',
}

export interface TimeSlot {
  label: string;
  time: string;
}

@Component({
  selector: 'pl-timepicker',
  templateUrl: './pl-timepicker.component.html',
  styleUrls: ['./pl-timepicker.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [opacityAnimation],
})
export class PlTimepickerComponent
  extends NgOnInitWriteBugAbstractComponent<string>
  implements OnInit, OnChanges, ControlValueAccessor
{
  @Input() public format: TimeFormat = TimeFormat.twentyFourHours;
  @Input() public icon: PlIcon | null = PlIcon.time;
  @Input() public isDisabled: boolean = false;
  @Input() public isFormSubmitted: boolean = false;
  @Input() public label: string = '';
  @Input() public minutesGap: number = 30;
  @Input() public maxDurationInHours: number = 24; // use this to limit the number of options depending on "startTimeUsedForDuration" (or midnight if not provided)
  @Input() public startTimeUsedForDuration: string | null = null;

  public selectedTime: string = '';

  public identifier: string = `pl-timepicker-${identifier++}`;

  public readonly ButtonPadding: typeof ButtonPadding = ButtonPadding;
  public readonly ButtonVariant: typeof ButtonVariant = ButtonVariant;
  public readonly IconFrame: typeof IconFrame = IconFrame;
  public readonly PlIcon: typeof PlIcon = PlIcon;
  public readonly TimeFormat: typeof TimeFormat = TimeFormat;

  public readonly PositionX: typeof PositionX = PositionX;
  public readonly PositionY: typeof PositionY = PositionY;

  @HostBinding('tabindex') public tabindex: number = 1;
  @HostListener('focus') public onFocus(): void {
    this.inputElement.nativeElement.focus({ preventScroll: true });
  }

  public timeSlots: TimeSlot[] = [];

  @ViewChild('inputElement', { read: ElementRef }) public inputElement!: ElementRef;
  @ViewChildren('timeSlotElements', { read: ElementRef }) public timeSlotElements!: QueryList<ElementRef>;

  public test: boolean = true;

  public showMenu: WritableSignal<boolean> = signal(false);

  constructor(
    @Optional() @Self() public override controlDir: NgControl,
    private i18nService: I18nService,
  ) {
    super(controlDir);
  }

  public plOnInit(): void {
    if (this.controlDir) {
      const control: AbstractControl | null = this.controlDir.control;

      if (control) {
        const validators: ValidatorFn[] = control.validator ? [control.validator] : [];
        control.setValidators([this.validate.bind(this, validators)]);
        control.updateValueAndValidity({ emitEvent: false });
      }
    }

    this.timeSlots = this.getTimeSlots(this.startTimeUsedForDuration, this.format, this.maxDurationInHours);
  }

  public ngOnChanges(changes: ComponentChanges<PlTimepickerComponent>): void {
    if (changes.startTimeUsedForDuration) {
      this.timeSlots = this.getTimeSlots(
        changes.startTimeUsedForDuration.currentValue,
        this.format,
        this.maxDurationInHours,
      );
    }
  }

  private validate(validators: ValidatorFn[]): ValidationErrors | null {
    const allErrors: any = {};
    for (const validator of validators) {
      const formControl: UntypedFormControl = new UntypedFormControl(this.selectedTime);
      const hasError: ValidationErrors | null = validator(formControl);
      if (hasError) {
        Object.entries(hasError).forEach(([errorName, isOnError]: [string, boolean]) => {
          allErrors[errorName] = isOnError;
        });
      }
    }

    if (Object.keys(allErrors).length > 0) {
      return allErrors;
    }

    return null;
  }

  public plWriteValue(valueToWrite: string | undefined): void {
    if (valueToWrite !== undefined) {
      this.selectedTime = valueToWrite;
    }
    this.updateValueAndValidity();
  }

  public onTouched(): void {
    if (this.onTouchedCallback) {
      this.onTouchedCallback();
    }
  }

  public onTimeSlotClicked(timeClicked: string): void {
    this.onTouched();
    this.closeMenu();
    this.selectedTime = timeClicked;
    this.updateValueAndValidity();
    this.emitChanges();
  }

  public openDropdownToSelectTime(): void {
    this.showMenu.set(true);

    setTimeout(() => {
      const timeSlotToScrollTo: ElementRef | null = this.getTimeSlotToScrollToBasedOnSelectedTime(
        this.timeSlotElements,
        this.selectedTime,
      );

      if (timeSlotToScrollTo) {
        timeSlotToScrollTo?.nativeElement.scrollIntoView();
      }
    });
  }

  private getTimeSlotToScrollToBasedOnSelectedTime(
    timeSlotElements: QueryList<ElementRef>,
    selectedTime: string,
  ): ElementRef | null {
    for (let index: number = 0; index < timeSlotElements.length; index++) {
      const timeSlotElement: ElementRef | undefined = timeSlotElements.get(index);
      if (timeSlotElement) {
        const time: string = timeSlotElement.nativeElement.getAttribute('time');
        if (time === selectedTime) {
          if (index === 0) {
            return timeSlotElement;
          } else if (index === 1) {
            const previousTimeSlotElement: ElementRef | undefined = timeSlotElements.get(index - 1);
            if (previousTimeSlotElement) {
              return previousTimeSlotElement;
            }
          } else {
            const secondPreviousTimeSlotElement: ElementRef | undefined = timeSlotElements.get(index - 2);
            if (secondPreviousTimeSlotElement) {
              return secondPreviousTimeSlotElement;
            }
          }
        }
      }
    }

    return null;
  }

  private emitChanges(): void {
    this.onChangeCallback(this.selectedTime);
  }

  protected updateValueAndValidity(): void {
    if (this.controlDir.control) {
      this.controlDir.control.updateValueAndValidity({
        onlySelf: false,
        emitEvent: false,
      });
    }
  }

  public closeMenu(): void {
    this.showMenu.set(false);
  }

  private getTimeSlots(startTime: string | null, timeFormat: TimeFormat, maxDurationInHours: number): TimeSlot[] {
    const timeSlots: TimeSlot[] = [];

    let startDate: Date;
    let endDate: Date;
    let currentDate: Date;

    if (startTime) {
      startDate = new Date(`2024-01-29T${startTime}:00`);
      endDate = addMinutes(addHours(startDate, maxDurationInHours), 1);
      currentDate = addMinutes(startDate, this.minutesGap);
    } else {
      startDate = new Date(`2024-01-29T00:00:00`);
      endDate = addHours(startDate, maxDurationInHours);
      currentDate = new Date(startDate);
    }

    while (isBefore(currentDate, endDate)) {
      const time24h: string = format(currentDate, 'HH:mm');
      let label: string = timeFormat === TimeFormat.twentyFourHours ? time24h : convert24hTimeToAmPm(time24h);

      if (this.startTimeUsedForDuration) {
        const durationInMinutes: number = differenceInMinutes(currentDate, startDate);

        if (durationInMinutes < 60) {
          label += ` (${this.i18nService.translate(`components.timepicker.mins`, {
            count: durationInMinutes,
          })})`;
        } else {
          const durationInHours: number = durationInMinutes / 60;
          label += ` (${this.i18nService.translate(`components.timepicker.hours`, {
            count: durationInHours,
          })})`;
        }
      }

      timeSlots.push({
        time: time24h,
        label,
      });

      currentDate = addMinutes(currentDate, this.minutesGap);
    }

    return timeSlots;
  }
}
